import { useContext, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Button, Pop, PopOver } from "@vimeo/iris/components";
import { DotsMenu } from "@vimeo/iris/icons";

import type { MenuItem, MenuItemItem } from "prop-types";
import { Video } from "vimeo";

import {
    getVideoIdFromUri,
    onTrimVideoClicked,
    openCreateGifWidget,
    openCreateThumbnailWidget,
} from "../common";

import { getContentBlockType, isContentBlockContext } from "../sessionStorage";

import EmbedInEmailContext from "../contexts/embedInEmailContext";
import { EventName, sendAnalyticsEvent } from "../analytics/common";

import "./videoactionsmenu.scoped.scss";

interface Props {
    video: Video;
    onVideoTrimmerOpened: (opened: Window) => void;
    onShowEmbedInEmailModal: () => void;
}

const trimVideoAction = "trim-video";
export const embedInEmailAction = "embed-in-email";

export function VideoActionsMenu(props: Props) {
    const [_embedInEmailContextState, setEmbedInEmailContextState] =
        useContext(EmbedInEmailContext);

    const [showMenu, setShowMenu] = useState(false);

    const navigate = useNavigate();
    const { video, onVideoTrimmerOpened, onShowEmbedInEmailModal } = props;

    const contentBlockContext = isContentBlockContext();
    const contentBlockType = getContentBlockType();

    function getVideoOptions(): MenuItem {
        const canEdit = video.metadata?.interactions?.edit;
        const options: MenuItem[] = [
            {
                label: "Trim video",
                value: `${video.uri}/${trimVideoAction}`,
            },
            { type: "divider" },
            {
                label: "View video",
                value: `https://vimeo.com/manage${video.uri}`,
            },
            {
                label: "Settings",
                value: `${video.uri}/settings/basic`,
                disabled: !canEdit,
            },
        ];

        if (!contentBlockContext) {
            options.splice(
                0,
                0,
                {
                    label: "Create GIF",
                    value: `${video.uri}/create-gif`,
                },
                {
                    label: "Create thumbnail",
                    value: `${video.uri}/create-thumbnail`,
                },
                {
                    label: "Embed in email",
                    value: `${video.uri}/${embedInEmailAction}`,
                }
            );
        } else if (contentBlockType === "email") {
            options.splice(0, 0, {
                label: "Embed in email",
                value: `${video.uri}/${embedInEmailAction}`,
            });
        }

        return options;
    }

    async function onSelect(item: MenuItemItem) {
        if (item.disabled) {
            return;
        }

        setShowMenu(false);

        const videoId = getVideoIdFromUri(video.uri);

        if (item.value.startsWith("https://")) {
            if (item.label.toLowerCase() === "view video") {
                sendAnalyticsEvent(EventName.integrationsClick, {
                    name: "view_video",
                    location: "app_home",
                    video_id: videoId,
                    cta: "view_video",
                    asset_type: null,
                    copy: null
                }, 3);
            }

            window.open(item.value, "_blank");
            return;
        }

        // When the Embed in Email action is selected, open
        // it as a modal if we are NOT in a Content Block
        // context. Otherwise, navigate to that route
        // normally.
        if (item.value.includes(embedInEmailAction)) {
            setEmbedInEmailContextState({
                video,
            });

            sendAnalyticsEvent(EventName.openEmbedInEmail, {
                path: null,
                is_my_videos: null,
                clip_id: videoId,
                location: "app_home",
            }, 3);

            if (!isContentBlockContext()) {
                onShowEmbedInEmailModal();
                return;
            }
        }

        if (item.value.endsWith(trimVideoAction)) {
            sendAnalyticsEvent(EventName.integrationsClick, {
                name: "open_trimmer_modal",
                location: "app_home",
                video_id: videoId,
                cta: "trim_video",
                asset_type: null,
                copy: null
            }, 3);

            const opened = await onTrimVideoClicked(videoId);

            if (!opened) {
                return;
            }

            onVideoTrimmerOpened(opened);

            return;
        }

        if (item.value.endsWith("create-gif")) {
            sendAnalyticsEvent(EventName.integrationsClick, {
                name: "open_gif_modal",
                location: "app_home",
                video_id: videoId,
                cta: "create_gif",
                asset_type: null,
                copy: null
            }, 3);

            await openCreateGifWidget(videoId);
            return;
        }

        if (item.value.endsWith("create-thumbnail")) {
            sendAnalyticsEvent(EventName.integrationsClick, {
                name: "open_thumbnail_modal",
                location: "app_home",
                video_id: videoId,
                cta: null, // TODO: add create_thumbnail as enum in schema
                asset_type: null,
                copy: null
            }, 3);

            await openCreateThumbnailWidget(videoId);
            return;
        }

        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "video_settings",
            location: "app_home",
            video_id: videoId,
            cta: "settings",
            asset_type: null,
            copy: null
        }, 3);

        navigate(`${item.value}`);
    }

    return (
        <PopOver
            active={showMenu}
            content={
                <Pop.List>
                    {getVideoOptions().map((menuItem: MenuItem, i: number) => {
                        if (menuItem.type === "divider") {
                            return <Pop.Divider key={i}></Pop.Divider>;
                        }

                        return (
                            <Pop.Item
                                key={i}
                                onClick={async () => await onSelect(menuItem)}
                                data-value={menuItem.value}
                                data-menuitem="true"
                            >
                                <div
                                    className={
                                        menuItem.disabled
                                            ? "item-disabled"
                                            : undefined
                                    }
                                >
                                    {menuItem.label}
                                </div>
                            </Pop.Item>
                        );
                    })}
                </Pop.List>
            }
        >
            <Button
                format="secondary"
                icon={<DotsMenu />}
                size="sm"
                onClick={() => setShowMenu(!showMenu)}
            />
        </PopOver>
    );
}
